import ShopFnbFacilityCarouselEmpty from '@/components/common/shop-fnb-facility/carousel-empty';
import ShopFnbFacilityHeader from '@/components/common/shop-fnb-facility/header';
import { FacilityCategoryDetailModal, VirtualizedFacilityCategoryCarousel } from '@/components/feature/facility';
import { FloorGuideType } from '@/enums/floor-guide';
import { useCurrentCustomMenu, useFacilityCategories } from '@/hooks/custom-menu';
import { useOrientation } from '@/hooks/useOrientation';
import { PATHS } from '@/router/paths';
import type { BuildingMenu } from '@/services/dto';
import { useConfigStore } from '@/stores';
import { useFloorGuideStore } from '@/stores/floorGuideStore';
import type { FacilityCategoryItem } from '@/types/facility-category';
import { resolveKioskCategoryFromFacilityItem } from '@/utils/facilityCategory';
import clsx from 'clsx';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styles from './FacilityCategoryTypePage.module.scss';

interface FacilityCategoryTypePageProps {
    gnbMenuId?: string;
}

const FacilityCategoryTypePage = ({ gnbMenuId }: FacilityCategoryTypePageProps) => {
    const { t } = useTranslation();
    const ui = useConfigStore((state) => state.config.ui);
    const { isHorizontal } = useOrientation();
    const navigate = useNavigate();
    const { setSelectedCategoryNavigation, setFloorGuideType } = useFloorGuideStore();

    const { currentMenu } = useCurrentCustomMenu(gnbMenuId);
    const { categories } = useFacilityCategories(gnbMenuId);

    const [isOpenModal, setIsOpenModal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<FacilityCategoryItem | null>(null);

    const isHalfMode = ui.screenMode === 'split';

    const pageClassname = useMemo(
        () =>
            clsx(styles['facility-category'], {
                [styles['facility-category--vertical']]: !isHorizontal,
                [styles['facility-category--horizontal']]: isHorizontal,
                [styles['facility-category--contrast']]: ui.contrast,
                [styles['facility-category--horizontal--halfmode']]: isHalfMode && isHorizontal,
                [styles['facility-category--vertical--halfmode']]: isHalfMode && !isHorizontal,
            }),
        [isHalfMode, ui.contrast, isHorizontal]
    );

    const carouselClassname = useMemo(
        () =>
            clsx(styles['facility-category__carousel'], {
                [styles['facility-category__carousel--horizontal']]: isHorizontal,
                [styles['facility-category__carousel--vertical']]: !isHorizontal,
                [styles['facility-category__carousel--contrast']]: ui.contrast,
                [styles['facility-category__carousel--horizontal--halfmode']]: isHalfMode && isHorizontal,
                [styles['facility-category__carousel--vertical--halfmode']]: isHalfMode && !isHorizontal,
            }),
        [isHorizontal, isHalfMode, ui.contrast]
    );

    const menuName = (currentMenu?.name as string) || '';
    const menuDescription = (currentMenu as BuildingMenu | undefined)?.description || '';

    const handleCategoryClick = useCallback((category: FacilityCategoryItem) => {
        setSelectedCategory(category);
        setIsOpenModal(true);
    }, []);

    const handleLocationCheck = useCallback(() => {
        if (!selectedCategory) return;
        const kioskCategory = resolveKioskCategoryFromFacilityItem(
            selectedCategory,
            (currentMenu as BuildingMenu | undefined)?.selectedCategories
        );
        if (!kioskCategory) return;
        setSelectedCategoryNavigation(kioskCategory);
        setFloorGuideType(FloorGuideType.CATEGORY);
        navigate(PATHS.FLOOR_GUIDE.INDEX);
        setIsOpenModal(false);
    }, [selectedCategory, currentMenu, navigate, setFloorGuideType, setSelectedCategoryNavigation]);

    const handleCloseModal = useCallback(() => {
        setIsOpenModal(false);
    }, []);

    return (
        <div className={pageClassname}>
            <div
                className={clsx(styles['facility-category__header'], {
                    [styles['facility-category__header--horizontal']]: isHorizontal,
                    [styles['facility-category__header--vertical']]: !isHorizontal,
                    [styles['facility-category__header--vertical--halfmode']]: !isHorizontal && ui.screenMode === 'split',
                })}
            >
                <ShopFnbFacilityHeader title={menuName} description={menuDescription} isHorizontal={isHorizontal} tabIndex={1} />
            </div>

            <div className={carouselClassname}>
                {categories.length > 0 ? (
                    <VirtualizedFacilityCategoryCarousel contents={categories} onClickCategory={handleCategoryClick} />
                ) : (
                    <ShopFnbFacilityCarouselEmpty
                        id="facility-category-empty-result"
                        contrast={ui.contrast}
                        halfMode={ui.screenMode === 'split'}
                        isHorizontal={isHorizontal}
                        tabIndex={2}
                        textEmpty={t('custom_menu.empty')}
                        ariaLabel={t('custom_menu.empty')}
                    />
                )}
            </div>

            <FacilityCategoryDetailModal
                isOpen={isOpenModal}
                category={selectedCategory}
                onClose={handleCloseModal}
                onLocationCheck={handleLocationCheck}
            />
        </div>
    );
};

export default FacilityCategoryTypePage;
